<template>
  <div class="app-page">
    <HeaderDve ref="header" helpURL="studio/recycle/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </HeaderDve>
    <div class="main-page-div">
      <div class="filter-sidebar">
        <div class="filter-sidebar__container">
          <ul>
            <li class="filter-sidebar__item chkbox-list scrollbar">
              <span class="title">Item Type</span>
              <div class="type-clear-all" @click="toggleTypes"><span>{{ selectedTypes.length > 0 ? 'Clear All' : 'Select All' }}</span></div>
              <div
                v-for="(s, i) in types"
                :key="`type-checkbox-${i}`"
              >
                <div class="chkbx-li">
                  <div class="label-icon" @click.stop.prevent="selectType(s.value)">
                    <SVGIcon
                      :iconName="s.icon"
                      :width="'20px'"
                      :height="'20px'"
                    />
                    <div class="text">
                      {{ s.name }}
                    </div>
                  </div>
                  <div class="checkbox">
                    <v-checkbox
                      v-model="selectedTypes"
                      :value="s.value"
                      :hide-details="true"
                      density="compact"
                    />
                  </div>
                </div>
              </div>
            </li>
          </ul>
          <ul>
            <li class="filter-sidebar__item chkbox-list scrollbar">
              <span class="title">Date Removed</span>
              <div
                v-for="(s, i) in dates"
                :key="`type-checkbox-${i}`"
              >
                <div class="chkbx-li">
                  <div class="label-icon" @click.stop.prevent="selectDate(s.value)">
                    <div class="text">
                      {{ s.name }}
                    </div>
                  </div>
                  <div class="checkbox">
                    <v-checkbox
                      v-model="selectedDate"
                      :value="s.value"
                      :hide-details="true"
                      density="compact"
                    />
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <main class="main-display-area">
        <div class="controls">
          <div class="controls__start">
            <span class="controls__count">Recycled Items ({{ items.length }})</span>
          </div>
          <div class="controls__end">
            <div class="controls__actions">
              <button class="button" @click="listDeletedItems">
                <SVGIcon
                  :iconName="'refresh'"
                  :width="'18px'"
                  :height="'18px'"
                />Refresh
              </button>
              <button
                class="button"
                :disabled="selectedItems.size === 0"
                @click="handleRestoreAll"
              >
                <SVGIcon
                  :iconName="'restore'"
                  :width="'18px'"
                  :height="'18px'"
                />
                Restore
              </button>
              <button
                class="button delete"
                :disabled="selectedItems.size === 0"
                @click="handleDeleteAll"
              >
                <SVGIcon
                  :iconName="'trash'"
                  :width="'18px'"
                  :height="'18px'"
                />
                Delete
              </button>
            </div>
          </div>
        </div>
        <div class="data-table scrollbar">
          <div
            v-if="isLoading"
            class="data-table__loading"
            role="status"
            aria-label="Loading recycled items"
          >
            <InlineLoader :width="'60px'" :height="'60px'" :border="'8px'" />
          </div>
          <v-data-table
            v-else
            v-model:page="currentPage"
            v-model:items-per-page="itemsPerPage"
            :headers="headers"
            :items="items"
            fixed-header
            density="comfortable"
          >
            <template #headers="{ columns }">
              <tr>
                <template v-for="column in columns" :key="column.key">
                  <th class="th2">
                    <template v-if="column.key === 'date_removed'">
                      <span class="mr-2 cursor-pointer" @click="() => sortDateAsc = !sortDateAsc">
                        {{ column.title }}
                        <v-icon size="16px" :icon="getSortIcon()" />
                      </span>
                    </template>
                    <template v-else-if="column.key === 'select'">
                      <div class="select-all checkbox">
                        <v-checkbox
                          :model-value="allPageItemsSelected"
                          :indeterminate="somePageItemsSelected"
                          :hide-details="true"
                          density="compact"
                          :disabled="items && items.length === 0"
                          @update:model-value="handleSelectAllClicked"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <span class="mr-2 ">{{ column.title }}</span>
                    </template>
                  </th>
                </template>
              </tr>
            </template>
            <template #[`item.description`]="{ item }">
              <div class="label-icon" :title="item.description">
                <SVGIcon
                  :iconName="item.icon"
                  :width="'24px'"
                  :height="'24px'"
                  class="data-table-icon"
                />
                <div class="text">
                  {{ item.description }}
                </div>
              </div>
            </template>
            <template #[`item.select`]="{ item }">
              <div class="select-all checkbox">
                <v-checkbox
                  :model-value="selectedItems.has(item.id)"
                  :hide-details="true"
                  density="compact"
                  @update:model-value="toggleItemSelection(item.id, $event)"
                />
              </div>
            </template>
            <template #[`item.project`]="{ item }">
              <div class="project-details">
                <div>{{ item.project }}</div>
                <div class="project-uid">{{ item.project_uid }}</div>
              </div>
            </template>
            <template #[`item.actions`]="{ item }">
              <div class="action-icons-container">
                <div :title="`Restore ${item.description}`">
                  <SVGIcon
                    class="action-icon"
                    :iconName="'restore'"
                    :width="'24px'"
                    :height="'24px'"
                    @click.stop="undelete([item])"
                  />
                </div>
                <div :title="`Permanently remove ${item.description}`">
                  <SVGIcon
                    class="action-icon"
                    :iconName="'trash'"
                    :width="'24px'"
                    :height="'24px'"
                    @click.stop="confirmPurge(item)"
                  />
                </div>
              </div>
            </template>
          </v-data-table>
        </div>
      </main>
    </div>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="confirmButtonClass"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <TasksModal />
  <RecycleStatusDialog
    ref="recycleStatusDialog"
    :enterpriseServerUrl="$store.state.enterpriseServerUrl"
    @complete="handlePurgeComplete"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import HeaderDve from '@/components/Header.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import TasksModal from '@/components/TasksStatusComponent/TasksModalV2.vue';
import RecycleStatusDialog from '@/components/RecycleStatusDialog.vue';
import InlineLoader from '@/components/InlineLoader.vue';

export default {
  name: 'RecycleBin',
  components: {
    HeaderDve,
    BreadcrumbNavigation,
    SVGIcon,
    ConfirmModal,
    TasksModal,
    RecycleStatusDialog,
    InlineLoader,
  },
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      types: [
        {
          name: "Project",
          value: "project",
          icon: "folder",
        },
        {
          name: "Dataset",
          value: "dataset",
          icon: "database2",
        },
        {
          name: "Annotation Set",
          value: "annset",
          icon: "set",

        },
        {
          name: "Model Experiment",
          value: "experiment",
          icon: "science",
        },
        {
          name: "Train Session",
          value: "train-session",
          icon: "train",
        },
        // {
        //   name: "Automated Task",
        //   value: "auto-task",
        //   icon: "auto_annotate",
        // },
        {
          name: "Validate Session",
          value: "validate",
          icon: "compare",
        },
      ],
      selectedTypes: [],
      dates: [
        {
          name: "Today",
          value: "today",
        },
        {
          name: "7 Days",
          value: "7d",
        },
        {
          name: "30 Days",
          value: "30d",
        },
        {
          name: "Current Month",
          value: "current-month",
        },
        {
          name: "Current Year",
          value: "current-year",
        },
        {
          name: "All Time",
          value: "all-time",
        },
      ],
      selectedDate: "all-time",
      sortDateAsc: false,
      headers: [
        {
          title: 'selection', key: 'select', width: '1%', align: 'start',
        },
        {
          title: 'UID', key: 'id', width: '18%', align: 'start',
        },
        {
          title: 'Description', key: 'description', width: '28%', align: 'start',
        },
        {
          title: 'Project', key: 'project', width: '25%', align: 'start',
        },
        {
          title: 'Date Removed', key: 'date_removed', sortable: true, width: '20%', align: 'start',
        },
        {
          title: '', key: 'actions', width: '8%', align: 'end',
        },
      ],
      items: [],
      userList: [],
      controller: new AbortController(),
      confirmMessage: null,
      confirmMessageHeader: null,
      confirmButtonClass: null,
      confirmFunction: null,
      buttonText: 'Purge',
      selectedItems: new Set(),
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 50,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    projectList() {
      return this.$store.state.projects.projectList;
    },
    currentPageItems() {
      if (this.itemsPerPage < 0) {
        return this.items;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.items.slice(start, start + this.itemsPerPage);
    },
    allPageItemsSelected() {
      return this.currentPageItems.length > 0 && this.currentPageItems.every((item) => this.selectedItems.has(item.id));
    },
    somePageItemsSelected() {
      return !this.allPageItemsSelected && this.currentPageItems.some((item) => this.selectedItems.has(item.id));
    },
  },
  watch: {
    selectedDate() {
      this.listDeletedItems();
    },
    selectedTypes() {
      this.listDeletedItems();
    },
    sortDateAsc(val) {
      this.sortByDate(val);
    },
  },
  created() {
    this.types.forEach((t) => {
      if (t.value !== "auto-task") {
        this.selectedTypes.push(t.value);
      }
    });
    this.getUserList();
    this.listDeletedItems();
  },
  methods: {
    getSortIcon() {
      return this.sortDateAsc ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },
    handleSelectAllClicked(selected) {
      if (selected) {
        this.currentPageItems.forEach((item) => this.selectedItems.add(item.id));
      } else {
        this.currentPageItems.forEach((item) => this.selectedItems.delete(item.id));
      }
    },
    toggleItemSelection(id, selected) {
      if (selected) {
        this.selectedItems.add(id);
      } else {
        this.selectedItems.delete(id);
      }
    },
    sortByDate(val) {
      const temp = [...this.items];
      if (val) {
        temp.sort((a, b) => new Date(a.date_removed) - new Date(b.date_removed));
      } else {
        temp.sort((a, b) => new Date(b.date_removed) - new Date(a.date_removed));
      }
      this.items = [...temp];
    },
    async listDeletedItems() {
      let deleted_start = "";
      let deleted_end = "";
      let current = new Date();

      switch (this.selectedDate) {
      case 'today':
        deleted_end = current.toISOString();
        current.setHours(0, 0, 0, 0);
        deleted_start = current.toISOString();
        break;
      case '7d':
        deleted_end = current.toISOString();
        current.setDate(current.getDate() - 7);
        deleted_start = current.toISOString();
        break;
      case '30d':
        deleted_end = current.toISOString();
        current.setDate(current.getDate() - 30);
        deleted_start = current.toISOString();
        break;
      case 'current-month':
        deleted_end = current.toISOString();
        current.setDate(1);
        current.setHours(0, 0, 0, 0);
        deleted_start = current.toISOString();
        break;
      case 'current-year':
        deleted_end = current.toISOString();
        current = new Date(Date.UTC(current.getFullYear(), 0, 1));
        current.setHours(0, 0, 0, 0);
        deleted_start = current.toISOString();
        break;
      case 'all-time':
        deleted_end = current.toISOString();
        deleted_start = (new Date(Date.UTC(0))).toISOString();
        break;
      default:
      }
      this.controller.abort();
      this.controller = new AbortController();
      const currentController = this.controller;
      this.isLoading = true;

      await this.dsConn.getListOfRecycledItems({
        types: this.selectedTypes,
        deleted_start,
        deleted_end,
      }, currentController.signal).then((data) => {
        if (data.result) {
          this.parseData(data.result);
        }
      }).catch((e) => console.log(e))
        .finally(() => {
          if (this.controller === currentController) {
            this.isLoading = false;
          }
        });
    },
    parseData(data) {
      this.items = [];
      this.selectedItems.clear();
      data['projects'].forEach((project) => {
        this.items.push({
          icon: 'folder',
          type: 'project',
          id: this.encode(project.id, 'project'),
          description: project.name,
          project: project.name,
          project_uid: this.encode(project.id, 'project'),
          date_removed: this.formatTime(project.deleted),
        });
      });
      data['datasets'].forEach((dataset) => {
        this.items.push({
          icon: 'database2',
          type: 'dataset',
          id: this.encode(dataset.id, 'dataset'),
          description: dataset.name,
          project: this.findProjectNameById(dataset.project_id),
          project_uid: this.encode(dataset.project_id, 'project'),
          date_removed: this.formatTime(dataset.deleted),
        });
      });
      data['annotation_sets'].forEach((annset) => {
        this.items.push({
          icon: 'set',
          type: 'annset',
          id: this.encode(annset.id, 'annotation set'),
          description: annset.name,
          project: this.findProjectNameById(annset.project_id),
          project_uid: this.encode(annset.project_id, 'project'),
          date_removed: this.formatTime(annset.deleted),
        });
      });
      data['experiments'].forEach((exp) => {
        this.items.push({
          icon: 'science',
          type: 'experiment',
          id: this.encode(exp.id, 'experiment'),
          description: exp.name,
          project: this.findProjectNameById(exp.project_id),
          project_uid: this.encode(exp.project_id, 'project'),
          date_removed: this.formatTime(exp.deleted),
        });
      });
      data['training_sessions'].forEach((session) => {
        this.items.push({
          icon: 'train',
          type: 'train-session',
          id: this.encode(session.id, 'trainer'),
          description: session.name,
          project: this.findProjectNameById(session.project_id),
          project_uid: this.encode(session.project_id, 'project'),
          date_removed: this.formatTime(session.deleted),
        });
      });
      data['automated_tasks'].forEach((task) => {
        this.items.push({
          icon: 'auto_annotate',
          type: 'auto-task',
          id: this.encode(task.id, 'auto annotation'),
          description: task.name,
          project: this.findProjectNameById(task.project_id),
          project_uid: this.encode(task.project_id, 'project'),
          date_removed: this.formatTime(task.deleted),
        });
      });
      data['validate_sessions'].forEach((session) => {
        this.items.push({
          icon: 'compare',
          type: 'validate',
          id: this.encode(session.id, 'validator'),
          description: session.docker_task ? session.docker_task.name : `Validation ${this.encode(session.id, 'validator')}`,
          project: this.findProjectNameById(session.project_id),
          project_uid: this.encode(session.project_id, 'project'),
          date_removed: this.formatTime(session.deleted),
        });
      });
      this.sortByDate(this.sortDateAsc);
    },
    encode(id, type) {
      if (id && type) {
        let prefix = '';
        switch (type) {
        case 'dataset':
          prefix = 'ds';
          break;
        case 'annotation set':
          prefix = 'as';
          break;
        case 'annotation':
          prefix = 'a';
          break;
        case 'image':
          prefix = 'i';
          break;
        case 'experiment':
          prefix = 'exp';
          break;
        case 'trainer':
          prefix = 't';
          break;
        case 'validator':
          prefix = 'v';
          break;
        case 'project':
          prefix = 'p';
          break;
        case 'auto annotation':
          prefix = 'inf';
          break;
        default:
          prefix = '';
        }
        let result = '';
        if (prefix !== '') {
          result = `${prefix}-${Number(id).toString(16)}`;
        } else {
          result = Number(id).toString(16);
        }

        return result;
      }
      return '';
    },
    decode(id) {
      if (typeof id === 'number') {
        return id;
      } else {
        const valueHex = id.split("-")[1];
        const value = parseInt(valueHex, 16);
        return value;
      }
    },
    async getUserList() {
      await this.dsConn.getUserListByOrganization({ organization_id: this.user.organization_id })
        .then((data) => {
          if (data.result) {
            this.userList = data.result.reverse();
          }
        }).catch((e) => console.log(e));
    },
    findProjectNameById(id) {
      if (this.projectList && this.projectList.length > 0) {
        const project = this.projectList.find((e) => e.id === id);
        if (project) {
          return project.name;
        } else {
          return "";
        }
      } else { return ""; }
    },
    toggleTypes() {
      if (this.selectedTypes.length > 0) {
        this.selectedTypes = [];
      } else {
        const notIn = this.types.filter((e) => !this.selectedTypes.includes(e.value)).map((e) => e.value);
        this.selectedTypes = this.selectedTypes.concat(notIn);
      }
    },
    formatTime(str) {
      return new Date(str).toLocaleString('default', {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
      });
    },
    async undelete(items) {
      const ids = items.map((e) => e.id);
      await this.dsConn.undeleteRecycledItem({
        item_ids: ids,
      })
        .then(() => {
          this.listDeletedItems();
        });
    },
    confirmPurge(item) {
      this.$refs.recycleStatusDialog.showModal([item]);
    },
    handleDeleteAll() {
      const selectedItems = this.items.filter((item) => this.selectedItems.has(item.id));
      this.$refs.recycleStatusDialog.showModal(selectedItems);
      this.selectedItems.clear();
    },
    handlePurgeComplete() {
      this.listDeletedItems();
    },
    selectType(str) {
      const index = this.selectedTypes.indexOf(str);
      if (index !== -1) {
        this.selectedTypes = this.selectedTypes.filter((type) => type !== str);
      } else {
        this.selectedTypes.push(str);
      }
    },
    selectDate(str) {
      this.selectedDate = str;
    },
    handleRestoreAll() {
      this.confirmMessage = `Are you sure you want to restore all ${this.selectedItems.size} item(s)?`;
      this.confirmMessageHeader = 'Restore All';
      this.confirmButtonClass = null;
      this.buttonText = 'Restore';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.restoreAll();
      };
    },
    restoreAll() {
      const selectedItems = this.items.filter((item) => this.selectedItems.has(item.id));
      this.undelete(selectedItems);
      this.selectedItems.clear();
    },
  },
};
</script>

<style lang="scss" scoped>

.main-page-div {
  overflow-x: auto !important;
}

.main-display-area {
  overflow: visible !important;
}

.data-table__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.project-uid {
  margin-top: 2px;
  color: var(--color-gray-700);
  font-family: monospace;
  font-size: 0.8rem;
}

.filter-sidebar {
  min-width: 250px;
  width: 250px;
  @include themify() {
    background-color: themed('background-light-gray');
  };
  padding: 10px 10px 30px 10px;

  &__container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 100%;
    padding: 0 10px 0 10px;

    ul {
      list-style-type: none;
    }

    ul + ul {
      margin-top: 10px;
    }

    li {
      display: flex;
      flex-direction: row;
      margin-top: 2px;
      margin-bottom: 2px;
      font-size: 0.9rem;
      padding: 4px;
      border-radius: 4px;
    }
  }

  &__item{
    display: flex;
    flex-direction: column;
    color: var(--color-black) !important;
  }

  .chkbox-list {
      display: flex;
      flex-direction: column;
      flex: 1 1 50%;
      margin: 0;

      .title {
        font-size: 1.1rem;
      }

      .type-clear-all {
        display: flex;
        flex-direction: row-reverse;
        margin-top: 5px;
        span {
          font-size: 0.85rem;
          color: var(--color-primary);
          cursor: pointer;
        }
      }

      .chkbx-li {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        height: 35px;
      }

      .checkbox {
        width: 28px;
        max-width: 28px;
      }

    }
}

.label-icon {
  display: flex;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
  color: var(--color-black) !important;

  :nth-child(2) {
    margin-left: 5px;
  }

  .text {
    font-size: 0.9rem;
  }

  .data-table-icon {
    width: 24px;
    min-width: 24px;
  }
}

.action-icons-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  cursor: pointer;

  :nth-child(1) + :nth-child(2) {
    margin-left: 10px;
  }

  .action-icon {
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  .action-icon:hover {
    @include themify() {
      color: themed('color-primary');
      background: themed('icon-hover-color');
      border-radius: 100%;
      box-shadow: 0 0 0 4px themed('icon-hover-color');
    }
  }
}

.select-all {
  display: flex;
  flex-direction: row;
  width: 15px;
}

.date_removed {
  display: flex;
  flex-direction: row;

  .sort-icon {
    margin-left: 5px;
    cursor: pointer;

    &.desc{
      transform: rotate(180deg);
    }
  }
}

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  // .filter-sidebar {
  //   display: none;
  // }

  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 26px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;

  @include themify() {
    color: themed('body-text-color')
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &.delete:not([disabled]) {
    @include themify() {
      background: themed('button-delete');
      color: themed('button-delete-text');
    }
  }

  &[disabled] {
    border: none;
  }

  &.delete:hover:not([disabled]) {
    @include themify() {
      background: $color-red-300;
      color: themed('button-delete-text');
    }
  }
}

.data-table {
  width: calc(100% - 40px);
  height: 100%;
  padding: 0 15px 15px 15px;
  border-radius: 8px;
  margin: 0 20px 20px 20px;
  @include themify() {
    color: themed('body-text-color');
  }
  overflow-y: auto;
}

:deep(.v-table) {
  background: transparent;
  font-size: 0.95rem !important;
  border-radius: 0;
}

:deep(tbody > tr:hover) {
  background: var(--color-white-900);
}

:deep(th) {
  background: transparent !important;
  span {
    font-weight: 600;
    font-size: 1rem;
  }
}

:deep(th + th:not(:nth-last-of-type(-n+1))) {
  border-left-width: 2px;
  border-left-style: solid;
  border-image: linear-gradient(to bottom, rgba(108,219,141,0) 25%,rgba(74, 76, 75,1) 25%,rgba(74, 76, 75,1) 75%,rgba(108,219,141,0) 75%);
  border-image-slice: 1;
}

.cursor-pointer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

</style>
